import styled, { css } from 'foundations-theming/utils';
import { useCallback, useState, useRef, useEffect } from 'react';
import { useProxiedFetchGeneratedContent, useFetchContentAssistanceContent } from '../hooks/useFetchContentAssistanceContent';
import ContentAssistantLoadingScreen from '../screens/ContentAssistantLoadingScreen';
import PreviewGeneratedContent from '../screens/PreviewGeneratedContent';
import { FINISHED, STARTED, ERROR, REFRESH_ERROR } from '../hooks/constants';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { useInlineToolbarState } from './useInlineToolbarState';
import { useUsageTracker } from '../hooks/useUsageTracker';
import { MAX_RETRIES } from '../screens/constants';
import ContentAssistantError from '../screens/ContentAssistantError';
import BrandVoiceText from '../core/BrandVoiceText';
import { generateFeedbackAnnotatedResults } from '../api/contentAssistanceApi';
import { getBrandIdentityUrl } from '../utils/urls';
import { Link } from 'foundations-components/transitional/components/Link';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { tooltipBorder } from '../screens/SharedStyleComponents';

// Shared base styles for dropdown components
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const baseButtonStyles = css(["background-color:", ";line-height:", ";font-size:", ";font-weight:", ";user-select:none;border:0;border-radius:0;color:", ";text-align:left;width:100%;text-decoration:none;cursor:pointer;&:hover{background-color:", ";color:", ";}&:focus:not(:hover){background-color:", ";}"], ({
  theme
}) => theme.color['fill-surface-default'], ({
  theme
}) => theme.typography['body-300'].lineHeight, ({
  theme
}) => theme.typography['body-100'].fontSize, ({
  theme
}) => theme.typography['body-300'].fontWeight, ({
  theme
}) => theme.color['text-core-default'], ({
  theme
}) => theme.color['fill-field-hover'], ({
  theme
}) => theme.color['text-interactive-default-hover'], ({
  theme
}) => theme.color['fill-field-hover']);
const baseListStyles = css(["margin:0;padding:0;list-style:none;"]);
const menuItemCornerStyles = css(["", " ", ""], ({
  $isFirst,
  theme
}) => $isFirst && css(["border-top-left-radius:", ";border-top-right-radius:", ";"], theme.borderRadius['transitional-container'], theme.borderRadius['transitional-container']), ({
  $isLast,
  theme
}) => $isLast && css(["border-bottom-left-radius:", ";border-bottom-right-radius:", ";"], theme.borderRadius['transitional-container'], theme.borderRadius['transitional-container']));
const baseMenuContainerStyles = css(["padding:0;min-width:140px;background-color:", ";"], ({
  theme
}) => theme.color['fill-surface-default']);

// Custom dropdown menu component to avoid coupling with foundations-components private APIs
const CustomDropdownMenu = styled.div.withConfig({
  displayName: "InlineToolbar__CustomDropdownMenu"
})(["", ";", ";"], baseMenuContainerStyles, tooltipBorder);
const CustomDropdownList = styled.ul.withConfig({
  displayName: "InlineToolbar__CustomDropdownList"
})(["", ";"], baseListStyles);
const CustomDropdownItem = styled.li.withConfig({
  displayName: "InlineToolbar__CustomDropdownItem"
})(["margin:0;padding:0;"]);
const CustomDropdownButton = styled.button.withConfig({
  displayName: "InlineToolbar__CustomDropdownButton"
})(["", ";", ";display:flex;justify-content:space-between;align-items:center;min-height:40px;padding-block:", ";padding-inline:", ";", ""], baseButtonStyles, menuItemCornerStyles, ({
  theme
}) => theme.space['200'], ({
  theme
}) => theme.space['500'], ({
  disabled,
  theme
}) => disabled && css(["color:", ";cursor:not-allowed;&:hover{background-color:", ";color:", ";}"], theme.color['text-core-disabled'], theme.color['fill-surface-default'], theme.color['text-core-disabled']));
const SubMenuPopover = styled.div.withConfig({
  displayName: "InlineToolbar__SubMenuPopover"
})(["", ";position:absolute;left:100%;top:0;border:1px solid ", ";border-radius:", ";box-shadow:0 2px 8px rgba(0,0,0,0.1);overflow:hidden;z-index:1000;visibility:", ";opacity:", ";transform:", ";transition:all 0.15s ease-in-out;"], baseMenuContainerStyles, ({
  theme
}) => theme.color['border-core-subtle'], ({
  theme
}) => theme.borderRadius['transitional-container'], ({
  isVisible
}) => isVisible ? 'visible' : 'hidden', ({
  isVisible
}) => isVisible ? 1 : 0, ({
  isVisible
}) => isVisible ? 'none' : 'translateY(-4px)');
const SubMenuList = styled.ul.withConfig({
  displayName: "InlineToolbar__SubMenuList"
})(["", ";"], baseListStyles);
const SubMenuButton = styled.button.withConfig({
  displayName: "InlineToolbar__SubMenuButton"
})(["", ";display:block;min-height:32px;padding-block:", ";padding-inline:", ";"], baseButtonStyles, ({
  theme
}) => theme.space['200'], ({
  theme
}) => theme.space['400']);
const SubMenuTriggerWrapper = styled.div.withConfig({
  displayName: "InlineToolbar__SubMenuTriggerWrapper"
})(["position:relative;"]);
const MenuWrapper = styled.div.withConfig({
  displayName: "InlineToolbar__MenuWrapper"
})(["display:flex;flex-direction:row;"]);

// `This is used so MenuWrapper's click event isn't invoked
const StopPropagation = ({
  children
}) => {
  return /*#__PURE__*/_jsx("span", {
    onClick: e => e.stopPropagation(),
    children: children
  });
};
const createToolbarComponent = useFetchContent => {
  return ({
    brandVoiceOptions,
    onClosePopover,
    onTextChange,
    selectionCommands,
    editorContext,
    trackInteraction,
    usageDescription,
    language,
    objectId,
    settingsLink: __settingsLink,
    tips,
    tooltipClassName: __tooltipClassName
  }) => {
    const {
      state,
      actions
    } = useInlineToolbarState();
    const {
      onSuccess,
      setGeneratedContent,
      setSelectedCommand,
      incrementCount,
      setShowResult,
      setExtraParams
    } = actions;
    const {
      selectedCommand,
      generatedContent,
      regenerateClickCount,
      selectedResultIndex,
      allResults,
      previousExtraParams
    } = state;
    const onError = useCallback(e => {
      console.error('Something went wrong ', e);
    }, []);
    const {
      fetch,
      request,
      sendFeedback,
      error: __error,
      result: __result
    } = useFetchContent({
      onSuccess,
      onError,
      language,
      objectId
    });
    const trackUsage = useUsageTracker({
      trackInteraction,
      usageDescription
    });
    const handleFetch = useCallback((command, extraParams = {}) => {
      setSelectedCommand(command);
      setExtraParams(extraParams);
      fetch({
        commandId: command.commandId,
        parameters: Object.assign({
          editorContext
        }, extraParams)
      });
    }, [setSelectedCommand, fetch, editorContext, setExtraParams]);
    const {
      isSetUp,
      isEnabledForApp
    } = brandVoiceOptions || {};
    const usingBrandVoice = Boolean(isSetUp && isEnabledForApp);
    const handleOnClick = useCallback((command, extraParams = {}) => {
      const trackingParams = extraParams.toneOfVoice ? {
        choice: extraParams.toneOfVoice
      } : {};
      trackUsage('menu-selection', Object.assign({
        selected: command.commandId,
        usingBrandVoice
      }, trackingParams));
      handleFetch(command, extraParams);
    }, [handleFetch, trackUsage, usingBrandVoice]);
    const handleOnClickTryAgain = useCallback(() => {
      const command = selectedCommand;
      const trackingParams = previousExtraParams.toneOfVoice ? {
        choice: previousExtraParams.toneOfVoice
      } : {};
      trackUsage('click-try-again', Object.assign({
        selected: command.commandId
      }, trackingParams));
      handleFetch(command, previousExtraParams);
    }, [handleFetch, trackUsage, selectedCommand, previousExtraParams]);
    const handleRefresh = useCallback(() => {
      incrementCount();
      handleFetch(selectedCommand, previousExtraParams);
    }, [incrementCount, handleFetch, selectedCommand, previousExtraParams]);
    const handleClosePopover = useCallback(() => {
      onClosePopover();
      const feedbackAnnotatedResults = generateFeedbackAnnotatedResults(generatedContent, allResults, false);
      sendFeedback(feedbackAnnotatedResults);
    }, [allResults, generatedContent, onClosePopover, sendFeedback]);
    const submitCurrentText = useCallback(() => {
      const feedbackAnnotatedResults = generateFeedbackAnnotatedResults(generatedContent, allResults);
      sendFeedback(feedbackAnnotatedResults);
      // Should always(?) be true, but in case it isn't we should have some error fallback
      if (generatedContent && generatedContent.text) {
        onTextChange(generatedContent.text);
      }
    }, [generatedContent, allResults, sendFeedback, onTextChange]);

    // State for managing which submenu is currently open
    const [openSubmenuId, setOpenSubmenuId] = useState(null);
    const submenuTimeoutRef = useRef(undefined);

    // Handle submenu hover with delay for better UX
    const handleSubmenuEnter = useCallback(commandId => {
      if (submenuTimeoutRef.current) {
        clearTimeout(submenuTimeoutRef.current);
      }
      setOpenSubmenuId(commandId);
    }, []);
    const handleSubmenuLeave = useCallback(() => {
      submenuTimeoutRef.current = setTimeout(() => {
        setOpenSubmenuId(null);
      }, 150); // Small delay to allow moving to submenu
    }, []);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (submenuTimeoutRef.current) {
          clearTimeout(submenuTimeoutRef.current);
        }
      };
    }, []);

    // Custom menu component to render the dropdown without using private foundations APIs
    const renderCustomMenu = () => {
      return /*#__PURE__*/_jsx(CustomDropdownList, {
        children: selectionCommands.map((command, commandIndex) => {
          const isFirst = commandIndex === 0;
          const isLast = commandIndex === selectionCommands.length - 1;
          return /*#__PURE__*/_jsx(CustomDropdownItem, {
            children: command.disabled ? /*#__PURE__*/_jsxs(CustomDropdownButton, {
              disabled: true,
              $isFirst: isFirst,
              $isLast: isLast,
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: command.labelKey
              }), /*#__PURE__*/_jsx(HelpIcon, {
                "data-test-id": "disabled-command-icon",
                title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                  message: brandVoiceOptions !== null && brandVoiceOptions !== void 0 && brandVoiceOptions.isSetUp && !(brandVoiceOptions !== null && brandVoiceOptions !== void 0 && brandVoiceOptions.isEnabledForApp) ? 'baseContentGenerationModal.inlineToolbar.brandVoiceDisabledForAppTooltip_jsx' : 'baseContentGenerationModal.inlineToolbar.brandVoiceNotSetUpTooltip_jsx',
                  elements: {
                    UILink: Link
                  },
                  options: {
                    href: getBrandIdentityUrl(),
                    external: true
                  }
                }),
                marginInlineStart: "100"
              })]
            }) : command.subMenu ?
            /*#__PURE__*/
            // Render submenus with popover-style positioning
            _jsxs(SubMenuTriggerWrapper, {
              onMouseEnter: () => handleSubmenuEnter(command.commandId),
              onMouseLeave: handleSubmenuLeave,
              children: [/*#__PURE__*/_jsxs(CustomDropdownButton, {
                $isFirst: isFirst,
                $isLast: isLast,
                children: [/*#__PURE__*/_jsx(FormattedMessage, {
                  message: command.labelKey
                }), /*#__PURE__*/_jsx(RightIcon, {
                  size: "300",
                  color: "icon-core-subtle"
                })]
              }), /*#__PURE__*/_jsx(SubMenuPopover, {
                isVisible: openSubmenuId === command.commandId,
                onMouseEnter: () => handleSubmenuEnter(command.commandId),
                onMouseLeave: handleSubmenuLeave,
                children: /*#__PURE__*/_jsx(SubMenuList, {
                  children: command.subMenu.map((subMenuOption, subIndex) => /*#__PURE__*/_jsx("li", {
                    children: /*#__PURE__*/_jsx(SubMenuButton, {
                      onClick: () => handleOnClick(command, subMenuOption.parameters),
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: subMenuOption.labelKey
                      })
                    })
                  }, `${command.commandId}-${subIndex}`))
                })
              })]
            }) : /*#__PURE__*/_jsx(CustomDropdownButton, {
              $isFirst: isFirst,
              $isLast: isLast,
              onClick: () => handleOnClick(command, command.parameters),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: command.labelKey
              })
            })
          }, command.commandId);
        })
      });
    };
    let label = selectedCommand ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: selectedCommand.labelKey
    }) : null;
    if (selectedCommand && selectedCommand.beta) {
      label = /*#__PURE__*/_jsxs("div", {
        className: "align-center",
        children: [label, /*#__PURE__*/_jsx(Badge, {
          use: "beta",
          marginInlineStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "baseContentGenerationModal.inlineToolbar.betaBadge"
          })
        })]
      });
    }
    const brandVoiceLink = isSetUp && isEnabledForApp && selectedCommand && selectedCommand.brandVoiceLinkKey && /*#__PURE__*/_jsx(BrandVoiceText, {
      brandVoiceLinkKey: selectedCommand.brandVoiceLinkKey,
      brandVoiceOptions: brandVoiceOptions
    });
    if (request === STARTED) {
      return /*#__PURE__*/_jsx(ContentAssistantLoadingScreen, {
        label: label,
        body: brandVoiceLink,
        onCancel: onClosePopover,
        tips: tips
      });
    } else if (request === FINISHED) {
      const trackingProperties = {
        command: selectedCommand.commandId,
        // regenerateClickCount starts at 0 but should be tracked starting from 1
        regenerateClickCount: regenerateClickCount + 1,
        usingBrandVoice
      };
      if (previousExtraParams.toneOfVoice) {
        trackingProperties.choice = previousExtraParams.toneOfVoice;
      }
      if (allResults.length === 0) {
        return /*#__PURE__*/_jsx(ContentAssistantError, {
          label: label,
          body: brandVoiceLink,
          onCancel: onClosePopover,
          onTryAgain: handleOnClickTryAgain
        });
      }
      return /*#__PURE__*/_jsx(PreviewGeneratedContent, {
        content: generatedContent,
        updateContent: setGeneratedContent,
        onSubmit: submitCurrentText,
        onCancel: handleClosePopover,
        onRefresh: handleRefresh,
        isRefreshDisabled: regenerateClickCount >= MAX_RETRIES,
        label: label,
        body: brandVoiceLink,
        setShowResult: setShowResult,
        resultCount: allResults.length,
        trackUsage: trackUsage,
        selectedResultIndex: selectedResultIndex,
        trackingProperties: trackingProperties
      });
    } else if (request === ERROR || request === REFRESH_ERROR) {
      return /*#__PURE__*/_jsx(ContentAssistantError, {
        label: label,
        body: brandVoiceLink,
        onCancel: onClosePopover,
        onTryAgain: handleOnClickTryAgain
      });
    }

    // This is the floating inline toolbar version that shows up when select some text
    return (
      /*#__PURE__*/
      // Close the menu if the container is clicked, but the events inside handleClick prevent propagation, so this won't happen
      // if a button is clicked
      _jsx(MenuWrapper, {
        onClick: onClosePopover,
        children: /*#__PURE__*/_jsx(StopPropagation, {
          children: /*#__PURE__*/_jsx(CustomDropdownMenu, {
            children: renderCustomMenu()
          })
        })
      })
    );
  };
};
export const ProxiedInlineToolbar = createToolbarComponent(useProxiedFetchGeneratedContent);
export const InlineToolbar = createToolbarComponent(useFetchContentAssistanceContent);